/**
 * Light Theme Definition
 * Accessible light theme variant maintaining RPG aesthetic
 * Provides high contrast and WCAG AA compliance
 */

import { Theme, SemanticTokens } from '../tokens';
import { primitiveTokens } from '../tokens/primitive';
import { semanticSpacing, semanticSizing, semanticTypography, semanticAnimations, semanticMotion, semanticBreakpoints, semanticBorders } from '../tokens/semantic';

// Light theme semantic tokens (overrides for light theme)
const lightSemanticTokens: SemanticTokens = {
  colors: {
    // Text colors - inverted for light theme
    text: {
      primary: primitiveTokens.colors.gray[900],        // Dark text on light background
      secondary: primitiveTokens.colors.gray[700],      // Secondary dark text
      muted: primitiveTokens.colors.gray[500],          // Muted text
      inverse: primitiveTokens.colors.white,            // White text for dark areas
      disabled: primitiveTokens.colors.gray[400],       // Disabled text
    },
    
    // Background colors - light theme variants
    background: {
      primary: primitiveTokens.colors.white,            // White primary background
      secondary: primitiveTokens.colors.gray[50],       // Very light gray
      tertiary: primitiveTokens.colors.gray[100],       // Light gray
      card: primitiveTokens.colors.gray[50],            // Light card background
      cardHover: primitiveTokens.colors.gray[100],      // Hover state
      overlay: 'rgba(0, 0, 0, 0.5)',                   // Dark overlay for modals
      inverse: primitiveTokens.colors.gray[900],        // Dark background for special areas

      // Layered backgrounds for depth (light theme)
      elevated: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',                      // Elevated elements
      surface: 'rgba(0, 0, 0, 0.01)',                  // Surface elements
      surfaceHover: 'rgba(0, 0, 0, 0.03)',             // Surface hover state
      accent: 'rgba(59, 130, 246, 0.1)',               // Accent background (blue for light theme)
      hover: 'rgba(0, 0, 0, 0.05)',                    // Hover background
    },
    
    // Border colors - adapted for light theme
    border: {
      default: primitiveTokens.colors.gray[200],        // Light gray borders
      light: primitiveTokens.colors.gray[100],          // Very light borders
      heavy: primitiveTokens.colors.gray[300],          // Heavier borders
      focus: primitiveTokens.colors.red[500],           // Focus remains red
      error: primitiveTokens.colors.red[500],           // Error border
      success: primitiveTokens.colors.green[600],       // Success border
      warning: primitiveTokens.colors.orange[500],      // Warning border
    },
    
    // Interactive colors - maintain brand colors with good contrast
    interactive: {
      primary: primitiveTokens.colors.red[600],         // Primary red (accessible on light)
      primaryHover: primitiveTokens.colors.red[700],    // Darker on hover
      primaryActive: primitiveTokens.colors.red[800],   // Even darker when active
      secondary: primitiveTokens.colors.blue[600],      // Secondary blue
      secondaryHover: primitiveTokens.colors.blue[700],
      secondaryActive: primitiveTokens.colors.blue[800],
      accent: primitiveTokens.colors.orange[600],       // Accent orange
      accentHover: primitiveTokens.colors.orange[700],
      accentActive: primitiveTokens.colors.orange[800],
    },
    
    // Feedback colors - accessible versions
    feedback: {
      success: primitiveTokens.colors.green[600],       // Darker green for accessibility
      successHover: primitiveTokens.colors.green[700],
      warning: primitiveTokens.colors.orange[600],      // Darker orange
      warningHover: primitiveTokens.colors.orange[700],
      error: primitiveTokens.colors.red[600],           // Darker red
      errorHover: primitiveTokens.colors.red[700],
      'error-hover': primitiveTokens.colors.red[700],   // Alternative naming for consistency
      info: primitiveTokens.colors.blue[600],           // Darker blue
      infoHover: primitiveTokens.colors.blue[700],
    },
    
    // RPG-specific colors - maintain theme while ensuring accessibility
    rpg: {
      hp: primitiveTokens.colors.red[600],              // Accessible red for HP
      hpHover: primitiveTokens.colors.red[700],
      stress: primitiveTokens.colors.purple[600],       // Accessible purple for stress
      stressHover: primitiveTokens.colors.purple[700],
      armor: primitiveTokens.colors.blue[600],          // Accessible blue for armor
      armorHover: primitiveTokens.colors.blue[700],
      hope: primitiveTokens.colors.orange[600],         // Accessible orange for hope
      hopeHover: primitiveTokens.colors.orange[700],
      focus: primitiveTokens.colors.blue[700],          // Accessible blue for focus
      focusHover: primitiveTokens.colors.blue[800],

      // Priority system colors (light theme variants)
      priority: {
        high: '#d97706',       // Darker amber for light theme accessibility
        medium: '#7c3aed',     // Darker purple for light theme accessibility
        low: '#059669',        // Darker emerald for light theme accessibility
      },

      // Class-specific colors (light theme variants - darker for accessibility)
      classes: {
        warrior: '#dc2626',    // Darker red for light theme
        brawler: '#ea580c',    // Darker orange for light theme
        guardian: '#2563eb',   // Darker blue for light theme
        ranger: '#16a34a',     // Darker green for light theme
        rogue: '#7c3aed',      // Darker purple for light theme
        seraph: '#ca8a04',     // Darker yellow for light theme
        wizard: '#2563eb',     // Darker blue for light theme
        sorcerer: '#db2777',   // Darker pink for light theme
        bard: '#db2777',       // Darker pink for light theme
        druid: '#16a34a',      // Darker green for light theme
      },
    },

    // Status colors (additional semantic meanings)
    status: {
      success: primitiveTokens.colors.green[600],
      warning: primitiveTokens.colors.orange[600],
      error: primitiveTokens.colors.red[600],
      info: primitiveTokens.colors.blue[600],
    },
  },

  // Use theme-agnostic border tokens
  borders: semanticBorders,

  // Gradients for light theme
  gradients: {
    // Icon gradients (light theme variants)
    icons: {
      identity: 'linear-gradient(135deg, #1e40af 0%, #3730a3 100%)',      // Darker blue gradient for light theme
      appearance: 'linear-gradient(135deg, #0369a1 0%, #0891b2 100%)',    // Blue to teal gradient
      background: 'linear-gradient(135deg, #0891b2 0%, #0d9488 100%)',    // Teal to green gradient
      connections: 'linear-gradient(135deg, #0d9488 0%, #059669 100%)',   // Green gradient
      items: 'linear-gradient(135deg, #059669 0%, #16a34a 100%)',         // Green to lime gradient
      custom: 'linear-gradient(135deg, #16a34a 0%, #65a30d 100%)',        // Green variants for light theme
      journal: 'linear-gradient(135deg, #65a30d 0%, #84cc16 100%)',       // Yellow-green gradient
    },

    // Background gradients (light theme)
    backgrounds: {
      primary: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)',  // Light gradient
      secondary: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',             // Secondary light gradient
      card: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%)',                   // Card gradient
      elevated: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',              // Elevated element gradient
    },

    // Interactive gradients (light theme)
    interactive: {
      primary: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',       // Primary action gradient (vibrant red, still light enough for text)
      secondary: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',     // Secondary action gradient (vibrant blue, still light enough for text)
      accent: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',        // Accent gradient (vibrant orange, still light enough for text)
      hover: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',         // Hover state gradient
    },
  },

  // Enhanced shadows for light theme
  shadows: {
    base: '0 1px 3px rgba(0, 0, 0, 0.1)',          // Base shadow for light theme
    hover: '0 4px 6px rgba(0, 0, 0, 0.1)',         // Hover shadow
    elevated: '0 10px 15px rgba(0, 0, 0, 0.1)',    // Elevated shadow
    interactive: '0 4px 6px rgba(0, 0, 0, 0.1)',   // Interactive shadow
    focus: '0 0 0 3px rgba(59, 130, 246, 0.3)',    // Focus shadow for light theme
    // Elevation system shadows
    low: '0 2px 4px rgba(0, 0, 0, 0.1)',            // Low elevation
    medium: '0 4px 8px rgba(0, 0, 0, 0.15)',        // Medium elevation
    high: '0 8px 16px rgba(0, 0, 0, 0.2)',          // High elevation
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',     // Text shadow for depth
  },

  // Use theme-agnostic tokens
  spacing: semanticSpacing,
  sizing: semanticSizing,
  
  typography: semanticTypography,
  animations: semanticAnimations,
  motion: semanticMotion,
  breakpoints: semanticBreakpoints,
};


export const lightTheme: Theme = {
  metadata: {
    name: 'light',
    displayName: 'Light Theme',
    description: 'A bright, accessible theme maintaining the RPG aesthetic',
    version: '1.0.0',
    author: 'Søren Haug',
    isDark: false,
    isDefault: false,
  },
  tokens: {
    primitive: primitiveTokens,
    semantic: lightSemanticTokens,
  },
};

export default lightTheme;
